import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { LayoutDashboard, Briefcase, Shield, LogOut, Menu, X, Sun, Moon } from 'lucide-react';
import { cn } from '../lib/utils';
import { APP_VERSION } from '../version';
import { ClaudeConnectPrompt } from './ClaudeConnectPrompt';

interface LayoutProps {
    children: ReactNode;
}

const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Avant-ventes', href: '/opportunities', icon: Briefcase },
    // Réservé aux admins : filtré ci-dessous selon user.role.
    { name: 'Admin', href: '/admin', icon: Shield, adminOnly: true },
];

export function Layout({ children }: LayoutProps) {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const allNavigation = navigation.filter((item) => !item.adminOnly || user?.role === 'admin');

    const getInitials = (name?: string, email?: string) => {
        if (name) {
            return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
        }
        if (email) return email.split('@')[0].charAt(0).toUpperCase();
        return '?';
    };

    return (
        <div className="min-h-screen bg-[var(--color-bg)]">
            <header className="bg-[#020873] border-b border-[rgba(255,255,255,0.1)] sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-3">
                            <img src="/logo-clippit.png" alt="Clippit" className="h-10 w-auto" />
                            <span className="font-semibold text-lg text-white">AVV</span>
                        </Link>

                        <nav className="hidden md:flex items-center gap-1">
                            {allNavigation.map((item) => {
                                const isActive = location.pathname === item.href ||
                                    (item.href !== '/' && location.pathname.startsWith(item.href));
                                return (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        className={cn(
                                            'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                                            isActive
                                                ? 'bg-[rgba(255,255,255,0.15)] text-[#16ff2d]'
                                                : 'text-[rgba(255,255,255,0.8)] hover:text-[#16ff2d] hover:bg-[rgba(255,255,255,0.05)]'
                                        )}
                                    >
                                        {item.name}
                                    </Link>
                                );
                            })}
                        </nav>

                        <div className="flex items-center gap-3">
                            <div className="hidden sm:flex items-center gap-2">
                                <div className="w-8 h-8 bg-[rgba(255,255,255,0.15)] rounded-full flex items-center justify-center">
                                    <span className="text-white font-medium text-sm">
                                        {getInitials(user?.displayName, user?.email)}
                                    </span>
                                </div>
                                <span className="hidden lg:block text-sm font-medium text-white max-w-[160px] truncate">
                                    {user?.displayName}
                                </span>
                            </div>

                            <button
                                onClick={toggleTheme}
                                className="p-2 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                                title={theme === 'dark' ? 'Passer en mode clair' : 'Passer en mode sombre'}
                            >
                                {theme === 'dark' ? (
                                    <Sun className="w-4 h-4 text-[rgba(255,255,255,0.6)]" />
                                ) : (
                                    <Moon className="w-4 h-4 text-[rgba(255,255,255,0.6)]" />
                                )}
                            </button>

                            <button
                                onClick={logout}
                                className="p-2 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors"
                                title="Se déconnecter"
                            >
                                <LogOut className="w-4 h-4 text-[rgba(255,255,255,0.6)]" />
                            </button>

                            <button
                                className="md:hidden p-2 text-white hover:bg-[rgba(255,255,255,0.1)] rounded-lg"
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            >
                                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </button>
                        </div>
                    </div>
                </div>

                {mobileMenuOpen && (
                    <div className="md:hidden bg-[#020873] border-t border-[rgba(255,255,255,0.1)]">
                        <div className="px-4 py-3 space-y-1">
                            {allNavigation.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-[rgba(255,255,255,0.8)]"
                                    >
                                        <Icon className="w-5 h-5" />
                                        {item.name}
                                    </Link>
                                );
                            })}
                            <button
                                onClick={() => { setMobileMenuOpen(false); toggleTheme(); }}
                                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-[rgba(255,255,255,0.8)] w-full"
                            >
                                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                                {theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
                            </button>
                            <button
                                onClick={() => { setMobileMenuOpen(false); logout(); }}
                                className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-[rgba(255,255,255,0.8)] w-full"
                            >
                                <LogOut className="w-5 h-5" />
                                Se déconnecter
                            </button>
                        </div>
                    </div>
                )}
            </header>

            {/* Posée ici plutôt que dans chaque page : la question se pose une
                fois par session, quel que soit l'écran par lequel on entre. */}
            <ClaudeConnectPrompt />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {children}
            </main>

            <footer className="border-t border-[var(--color-border)] mt-auto">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
                    <p className="text-center text-xs text-[var(--color-text-muted)]">
                        Clippit AVV · v{APP_VERSION}
                    </p>
                </div>
            </footer>
        </div>
    );
}
